import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import StatusPill from '../components/StatusPill';
import { Search, ShoppingBag } from 'lucide-react';

const STATUSES = ['pending', 'confirmed', 'packed', 'shipped', 'out_for_delivery', 'delivered', 'cancelled', 'returned', 'refunded'];

export default function Orders() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');
    const [paymentStatus, setPaymentStatus] = useState('all');
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (q) params.q = q;
            if (status !== 'all') params.status = status;
            if (paymentStatus !== 'all') params.payment_status = paymentStatus;
            const r = await api.get('/orders', { params });
            setItems(r.data.items || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [status, paymentStatus]);

    return (
        <AppShell pageTitle="Orders">
            <Card className="p-3 sm:p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="relative md:col-span-2">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} placeholder="Search order number, customer, mobile" className="pl-9" data-testid="orders-search-input" />
                    </div>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger data-testid="orders-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                        <SelectTrigger><SelectValue placeholder="Payment" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All payments</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="unpaid">Unpaid</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </Card>

            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={ShoppingBag} title="No orders found" description="Try adjusting filters." />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="orders-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Order #</th>
                                <th className="text-left font-medium">Customer</th>
                                <th className="text-left font-medium">Type</th>
                                <th className="text-left font-medium">Items</th>
                                <th className="text-right font-medium">Total</th>
                                <th className="text-left font-medium">Payment</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-left p-3 font-medium">Placed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(o => (
                                <tr key={o.id} className="border-b hover:bg-neutral-50 cursor-pointer" onClick={() => nav(`/dharmikdashboard/orders/${o.id}`)} data-testid={`order-row-${o.order_number}`}>
                                    <td className="p-3 font-mono text-xs">{o.order_number}</td>
                                    <td>
                                        <div className="font-medium">{o.customer_snapshot?.name}</div>
                                        <div className="text-xs text-neutral-500">{o.customer_snapshot?.mobile}</div>
                                    </td>
                                    <td className="capitalize text-xs">{o.customer_snapshot?.type}{o.customer_snapshot?.level ? ` (L${o.customer_snapshot.level})` : ''}</td>
                                    <td>{o.items?.length}</td>
                                    <td className="text-right tabular-nums font-medium">₹{o.totals?.grand_total?.toLocaleString('en-IN')}</td>
                                    <td><StatusPill status={o.payment_status} /></td>
                                    <td><StatusPill status={o.status} /></td>
                                    <td className="p-3 text-xs text-neutral-600">{new Date(o.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}
        </AppShell>
    );
}
